/**
 * What the team editor says and keeps, apart from how it draws: field words for the platform's
 * `refused[]`, `ignored[]` and `changes[]` paths, the origin sentence, the vocabulary (with the
 * words the workbench's own lists give when the platform has not sent any), and the adapters
 * between the answers and the draft the editor works on.
 */

import { DELEGATION_WAIT_MAX, DELEGATION_WAIT_MIN, MEMBER_THINKING_LEVELS, ROLE_IDS, type OrgTeamMemberV2, type OrgTeamV2, type TeamDelegation } from '@aioe/shared';

import type { ProjectTeamDocument, TeamBody, TeamVocabulary } from '../../api/client.ts';
import { timeAgo } from '../../hooks.ts';

/** The words for one member or team field, as the platform names it in a path. */
const FIELD_WORDS: Record<string, string> = {
  name: 'name',
  description: 'description',
  lead: 'lead',
  kind: 'kind',
  roleId: 'catalogue role',
  role: 'role',
  roleKind: 'role',
  skillIds: 'skills',
  skills: 'skills',
  tools: 'tools',
  human: 'agent or person',
  subjectId: 'teammate',
  githubLogin: 'GitHub name',
  provider: 'runtime',
  model: 'model',
  runsOn: 'where it runs',
  providerId: 'provider',
  credential: 'provider key',
  account: 'provider account',
  thinkingLevel: 'thinking level',
  avatar: 'picture',
  useCharacter: 'picture',
  pictureUrl: 'picture',
  color: 'colour',
  agentInstructions: 'instructions',
  useWorktree: 'own worktree',
  permissionMode: 'permission mode',
  cwd: 'subfolder',
  ref: 'specialist',
  azureDevOpsUser: 'Azure DevOps user',
  formerNames: 'former names',
  memberOrder: 'order of members',
  entraAgentId: 'agent identity',
  sponsor: 'sponsor',
  a2a: 'Copilot access',
  grants: 'where it is granted',
  settings: 'team settings',
  'settings.orchestration': 'orchestration',
  'settings.approvals': 'approvals',
  'settings.permissionMode': 'permission mode',
  'settings.worktreeCleanup': 'worktree clean-up',
  'settings.maxConcurrentPlans': 'plans at once',
  'settings.taskTokenBudget': 'token budget per task',
  'settings.research': 'research',
  'settings.reviewPolicy': 'review policy',
  'settings.guidelines': 'guidelines',
};

/** `members.Coder.model` as "Coder's model"; `settings.guidelines` as "the team's guidelines". */
export function pathWords(path: string): string {
  const parts = path.split('.');
  if (parts[0] === 'members' && parts.length >= 3) {
    const field = parts.slice(2).join('.');
    return `${parts[1]}'s ${FIELD_WORDS[field] ?? FIELD_WORDS[parts[2]!] ?? parts.slice(2).join(' ')}`;
  }
  if (parts[0] === 'members' && parts.length === 2) return `the member ${parts[1]}`;
  const w = FIELD_WORDS[path] ?? FIELD_WORDS[parts[0]!];
  return w ? `the team's ${w}` : path.replace(/\./g, ' ');
}

/** "The platform kept its own model for Coder": one sentence for each field the platform would not change. */
export function refusedWords(refused: ReadonlyArray<{ path: string }>): string[] {
  return refused.map((r) => `The platform kept its own ${pathWords(r.path).replace(/^the team's /, '')}.`);
}

export function ignoredWords(ignored: readonly string[]): string {
  const words = [...new Set(ignored.map((p) => pathWords(p)))];
  return `The platform does not hold ${words.join(', ')}, so ${ignored.length === 1 ? 'it was' : 'they were'} left out.`;
}

/** A 400 `field_not_allowed { path }`, or any other refusal, as a sentence. */
export function saveErrorWords(e: unknown): string {
  const err = e as { status?: number; body?: { error?: string; path?: string; problems?: string[]; projects?: Array<{ id: string; name: string }>; message?: string }; message?: string };
  const b = err.body;
  if (b?.error === 'field_not_allowed') return `You cannot change ${b.path ? pathWords(b.path) : 'that field'} here; it is the platform's to set.`;
  if (b?.error === 'shared_team' || err.status === 409) {
    const names = b?.projects?.map((p) => p.name) ?? [];
    return names.length ? `This team is also used by ${names.join(', ')}. Ask a portfolio manager to change it.` : 'This team is used by other projects. Ask a portfolio manager to change it.';
  }
  return b?.problems?.join('. ') ?? err.message ?? 'The team could not be saved.';
}

const SCOPE_WORDS: Record<'project' | 'organisation', string> = { project: "saved with the repository", organisation: 'shared across their projects' };

/** "Defined in AI Workbench by JP for Mojo PSA, saved in the repository · managed with your organisation". */
export function originWords(team: Pick<OrgTeamV2, 'origin'>, projectName: (id: string) => string): string | undefined {
  const o = team.origin;
  if (!o) return undefined;
  const who = o.definedByName ?? o.definedBy;
  // A project-scoped team was defined in a workbench beside its repository; an organisation-scoped one may have been defined anywhere.
  return `Defined${o.scope === 'project' ? ' in AI Workbench' : ''}${who ? ` by ${who}` : ''} for ${projectName(o.projectId)}, ${SCOPE_WORDS[o.scope]} · managed with your organisation`;
}

/** The last change to a field, as "last changed by Ana Lopez 2 hours ago", or undefined. */
export function changeNote(team: Pick<OrgTeamV2, 'changes'>, path: string): string | undefined {
  const c = [...(team.changes ?? [])].reverse().find((x) => x.path === path);
  if (!c) return undefined;
  return `last changed by ${c.byName ?? c.by} ${timeAgo(c.at)}`;
}

const ROLE_WORDS_FALLBACK = ROLE_IDS.map((id) => ({ id: id as string, words: id.replace(/-/g, ' ') }));

/** What the platform sent, with the workbench's own lists where it sent nothing. */
export function withVocabulary(v: Partial<TeamVocabulary> | undefined): TeamVocabulary {
  return {
    roles: v?.roles?.length ? v.roles : ROLE_WORDS_FALLBACK,
    runtimes: v?.runtimes ?? [],
    thinkingLevels: v?.thinkingLevels?.length ? v.thinkingLevels : [...MEMBER_THINKING_LEVELS],
    models: v?.models ?? [],
  };
}

export const sentence = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

export const PERMISSION_WORDS: Record<string, string> = { supervised: 'Ask before each change', 'auto-edits': 'Edit files freely, ask for commands', auto: 'Run freely inside the project', full: 'No limits' };
export const THINKING_WORDS: Record<string, string> = { default: "The runtime's default", low: 'Low', medium: 'Medium', high: 'High', max: 'Maximum' };

export const blankMember = (): OrgTeamMemberV2 => ({ name: '', skillIds: [], human: false });

/** The platform's bookkeeping stays out of what a save sends. */
export function toBody(d: { name: string; description: string; lead: string; kind: 'team' | 'specialist'; members: OrgTeamMemberV2[]; settings?: OrgTeamV2['settings']; grants?: TeamBody['grants']; delegation?: TeamDelegation }): TeamBody {
  return {
    name: d.name.trim(),
    ...(d.description.trim() ? { description: d.description.trim() } : {}),
    ...(d.lead ? { lead: d.lead } : {}),
    kind: d.kind,
    members: d.members.map((m) => ({ ...m, name: m.name.trim() })),
    ...(d.settings && Object.keys(d.settings).length ? { settings: d.settings } : {}),
    ...(d.grants ? { grants: d.grants } : {}),
    // Left out, the platform keeps what it has; empty lists clear it.
    ...(d.delegation ? { delegation: d.delegation } : {}),
  };
}

/** An answer from either route, as the one shape the editor works on. */
export interface EditableTeam {
  id: string;
  name: string;
  description?: string;
  lead?: string;
  kind: 'team' | 'specialist';
  version: number;
  members: OrgTeamMemberV2[];
  settings?: OrgTeamV2['settings'];
  origin?: OrgTeamV2['origin'];
  changes?: OrgTeamV2['changes'];
  grants?: OrgTeamV2['grants'];
  memberSchema?: 2;
  /** Who answers for whom (teamDelegation.ts); absent from a platform before people in Team Work. */
  delegation?: TeamDelegation;
  owner?: { id: string; name?: string };
}

export function editable(t: OrgTeamV2 | ProjectTeamDocument): EditableTeam {
  const x = t as ProjectTeamDocument;
  return {
    id: x.id,
    name: x.name,
    description: x.description,
    lead: x.lead ?? x.orchestration?.opsManager,
    kind: x.kind ?? 'team',
    version: x.version,
    members: x.members.map((m) => ({ ...m, skillIds: m.skillIds ?? [], human: m.human ?? false })),
    settings: x.settings,
    origin: x.origin,
    changes: x.changes,
    grants: x.grants,
    memberSchema: x.memberSchema,
    ...((t as { delegation?: TeamDelegation }).delegation ? { delegation: (t as { delegation?: TeamDelegation }).delegation } : {}),
    ...((t as { owner?: { id: string; name?: string } }).owner ? { owner: (t as { owner?: { id: string; name?: string } }).owner } : {}),
  };
}

export const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

/** Said when a repair (Check this team) changed something, so the shell's count of teams to fix is asked again at once. */
export const TEAM_ATTENTION_CHANGED = 'aioe:team-attention-changed';

/** Whether a delegation draft can be saved as it stands: every topic named and answered, every cover given someone, waits in range. */
export function delegationIncomplete(d: TeamDelegation): string | undefined {
  if (d.topics.some((t) => !t.label.trim())) return 'Give every topic a name.';
  if (d.topics.some((t) => !t.answerers.length)) return 'Every topic needs someone to answer it.';
  if (d.cover.some((c) => !c.by.length)) return 'Every cover rule needs someone who covers.';
  const bad = (m: number | undefined) => m !== undefined && (m < DELEGATION_WAIT_MIN || m > DELEGATION_WAIT_MAX);
  if (d.topics.some((t) => bad(t.waitMinutes)) || bad(d.defaultWaitMinutes)) return `A wait is between ${DELEGATION_WAIT_MIN} and ${DELEGATION_WAIT_MAX.toLocaleString('en-AU')} minutes.`;
  return undefined;
}
